/**
 * Capture git WIP state before an agent turn via `git stash create` (no working tree change),
 * persist under `.buildautomaton/snapshots/`, and restore with reset + stash apply.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import type { LogFn } from '../../log.js';
import { discoverGitReposUnderRoot } from '../discover-repos.js';
import { forEachWithGitYield } from '../git-runtime.js';
import { gitRun, gitStashCreate, gitUntrackedPaths, type GitRunResult } from './git.js';
import {
  snapshotFilePath,
  snapshotsDirForCwd,
  type PreTurnSnapshotFile,
  type SnapshotRepoState,
} from './types.js';

export { snapshotFilePath } from './types.js';
export type { PreTurnSnapshotFile, SnapshotRepoState } from './types.js';

/**
 * Resolve unique git repo roots to snapshot (per-repo worktree checkouts and/or repos under session parent / bridge root).
 */
export async function resolveSnapshotRepoRoots(options: {
  worktreePaths: string[] | undefined;
  fallbackCwd: string;
  /** When set, a discover fallback under `fallbackCwd` keeps only git roots whose directory name equals this (session worktree layout). */
  sessionId?: string;
  log: LogFn;
}): Promise<string[]> {
  const { worktreePaths, fallbackCwd, sessionId, log } = options;
  if (worktreePaths?.length) {
    const uniq = [...new Set(worktreePaths.map((p) => path.resolve(p)))];
    return uniq;
  }
  try {
    const repos = await discoverGitReposUnderRoot(fallbackCwd);
    const mapped = repos.map((r) => r.absolutePath);
    const sid = sessionId?.trim();
    if (sid) {
      const filtered = mapped.filter((root) => path.basename(root) === sid);
      if (filtered.length > 0) return filtered;
    }
    return mapped;
  } catch (e) {
    log(`[snapshot] Discover repositories failed: ${e instanceof Error ? e.message : String(e)}`);
    return [];
  }
}

export async function capturePreTurnSnapshot(options: {
  runId: string;
  repoRoots: string[];
  agentCwd: string;
  log: LogFn;
}): Promise<{ ok: boolean; filePath?: string; repos?: SnapshotRepoState[]; error?: string }> {
  const { runId, repoRoots, agentCwd, log } = options;
  if (!runId || !repoRoots.length) {
    return { ok: false, error: 'No git repos to snapshot' };
  }
  const repos: SnapshotRepoState[] = [];
  await forEachWithGitYield(repoRoots, async (root) => {
    const stashSha = await gitStashCreate(root, log);
    const untrackedPaths = await gitUntrackedPaths(root, log);
    repos.push({ path: root, stashSha, untrackedPaths });
  });
  const dir = snapshotsDirForCwd(agentCwd);
  try {
    fs.mkdirSync(dir, { recursive: true });
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
  const payload: PreTurnSnapshotFile = {
    runId,
    createdAt: new Date().toISOString(),
    repos,
  };
  const filePath = path.join(dir, `${runId}.json`);
  try {
    fs.writeFileSync(filePath, JSON.stringify(payload, null, 2), 'utf8');
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
  const repoList = repos.map((r) => r.path).join(', ');
  log(
    `[snapshot] Saved pre-turn snapshot ${runId.slice(0, 8)}… (${repos.length} repo(s)): ${repoList}`,
  );
  return { ok: true, filePath, repos };
}

export async function applyPreTurnSnapshot(filePath: string, log: LogFn): Promise<{ ok: boolean; error?: string }> {
  let data: PreTurnSnapshotFile;
  try {
    const raw = fs.readFileSync(filePath, 'utf8');
    data = JSON.parse(raw) as PreTurnSnapshotFile;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
  if (!Array.isArray(data.repos)) {
    return { ok: false, error: 'Invalid snapshot file' };
  }
  let applyError: GitRunResult | null = null;
  await forEachWithGitYield(data.repos, async (r) => {
    if (applyError || !r.path) return;
    const reset = await gitRun(r.path, ['reset', '--hard', 'HEAD'], log, 'reset --hard');
    if (!reset.ok) {
      applyError = reset;
      return;
    }
    const clean = await gitRun(r.path, ['clean', '-fd'], log, 'clean -fd');
    if (!clean.ok) {
      applyError = clean;
      return;
    }
    if (r.stashSha) {
      const ap = await gitRun(r.path, ['stash', 'apply', r.stashSha], log, 'stash apply');
      if (!ap.ok) applyError = ap;
    }
  });
  if (applyError) return applyError;
  log(`[snapshot] Restored pre-turn state for ${data.runId.slice(0, 8)}…`);
  return { ok: true };
}
